import React, { FormEvent, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import type { Auth, ConfirmationResult, RecaptchaVerifier } from "firebase/auth";
import {
  createRecaptchaVerifier,
  getFirebaseAuth,
  sendSmsCode,
  signInWithApple,
  signInWithGoogle,
  type FirebaseConfig,
} from "./firebase";

const LOGIN_METHODS = {
  EMAIL: "email",
  GOOGLE: "google",
  APPLE: "apple",
  SMS: "sms",
} as const;

type LoginMethod = (typeof LOGIN_METHODS)[keyof typeof LOGIN_METHODS];
type ProviderLoginMethod = typeof LOGIN_METHODS.GOOGLE | typeof LOGIN_METHODS.APPLE;

type LoginAppProps = {
  sessionPath: string;
  firebaseSessionPath: string;
  returnTo: string;
  csrfToken: string;
  firebaseConfig: FirebaseConfig;
  assets: Record<LoginMethod | "logo", string>;
};

type AuthResponse = {
  redirect_to?: string;
  errors?: Array<{ message?: string }>;
};

const roots = new WeakMap<Element, ReturnType<typeof createRoot>>();

const methodLabels: Record<LoginMethod, string> = {
  [LOGIN_METHODS.EMAIL]: "メールアドレスでログイン",
  [LOGIN_METHODS.GOOGLE]: "Googleアカウントでログイン",
  [LOGIN_METHODS.APPLE]: "Appleアカウントでログイン",
  [LOGIN_METHODS.SMS]: "電話番号でログイン",
};

const methodOrder: LoginMethod[] = [LOGIN_METHODS.GOOGLE, LOGIN_METHODS.APPLE, LOGIN_METHODS.EMAIL, LOGIN_METHODS.SMS];

const providerSignInHandlers: Record<ProviderLoginMethod, (config: FirebaseConfig) => Promise<string>> = {
  [LOGIN_METHODS.GOOGLE]: signInWithGoogle,
  [LOGIN_METHODS.APPLE]: signInWithApple,
};

const providerDisplayNames: Record<ProviderLoginMethod, string> = {
  [LOGIN_METHODS.GOOGLE]: "Google",
  [LOGIN_METHODS.APPLE]: "Apple",
};

const providerButtonLabels: Record<ProviderLoginMethod, string> = {
  [LOGIN_METHODS.GOOGLE]: "Googleログイン画面を開く",
  [LOGIN_METHODS.APPLE]: "Appleログイン画面を開く",
};

function LoginApp(props: LoginAppProps) {
  const [method, setMethod] = useState<LoginMethod>(LOGIN_METHODS.EMAIL);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [smsSent, setSmsSent] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const authRef = useRef<Auth | null>(null);
  const verifierRef = useRef<RecaptchaVerifier | null>(null);

  const visibleAlternativeMethods = useMemo(() => methodOrder.filter((candidate) => candidate !== method), [method]);

  async function submitEmail(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await submitRequest(props.sessionPath, {
      email,
      password,
      return_to: props.returnTo,
    });
  }

  async function submitProvider(provider: ProviderLoginMethod) {
    setErrorMessage("");
    setIsSubmitting(true);

    try {
      const idToken = await providerSignInHandlers[provider](props.firebaseConfig);

      await submitFirebaseToken(idToken);
    } catch (error) {
      console.error(`${provider} sign-in failed`, error);
      setErrorMessage(providerErrorMessage(error, provider));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function submitSmsSend(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage("");
    setIsSubmitting(true);

    try {
      await requestSmsCode("phone-sign-in-button");
    } catch (error) {
      resetRecaptcha();
      setErrorMessage(smsErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function submitSmsCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!confirmationResult) return;

    setErrorMessage("");
    setIsSubmitting(true);

    try {
      const credential = await confirmationResult.confirm(smsCode);
      const idToken = await credential.user.getIdToken();
      await submitFirebaseToken(idToken);
    } catch (error) {
      setErrorMessage(smsErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function resendSmsCode() {
    setErrorMessage("");
    setIsSubmitting(true);

    try {
      await requestSmsCode("phone-resend-button");
    } catch (error) {
      resetRecaptcha();
      setErrorMessage(smsErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  function changePhoneNumber() {
    setSmsSent(false);
    setSmsCode("");
    setConfirmationResult(null);
    resetRecaptcha();
  }

  async function requestSmsCode(recaptchaTargetId: string) {
    const auth = getAuth();
    const verifier = getRecaptchaVerifier(auth, recaptchaTargetId);
    const result = await sendSmsCode(auth, toE164PhoneNumber(phoneNumber), verifier);
    setConfirmationResult(result);
    setSmsCode("");
    setSmsSent(true);
  }

  function getAuth() {
    authRef.current ||= getFirebaseAuth(props.firebaseConfig);
    return authRef.current;
  }

  function getRecaptchaVerifier(auth: Auth, recaptchaTargetId: string) {
    resetRecaptcha();
    verifierRef.current = createRecaptchaVerifier(auth, recaptchaTargetId);
    return verifierRef.current;
  }

  function resetRecaptcha() {
    verifierRef.current?.clear();
    verifierRef.current = null;
  }

  function resetSmsFlow() {
    setPhoneNumber("");
    changePhoneNumber();
  }

  function selectMethod(nextMethod: LoginMethod) {
    setErrorMessage("");
    if (nextMethod !== method) {
      resetSmsFlow();
    }
    setMethod(nextMethod);
  }

  async function submitFirebaseToken(idToken: string) {
    await submitRequest(props.firebaseSessionPath, {
      id_token: idToken,
      return_to: props.returnTo,
    });
  }

  async function submitRequest(path: string, body: Record<string, string>) {
    setErrorMessage("");
    setIsSubmitting(true);

    try {
      const response = await fetch(path, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": props.csrfToken,
        },
        credentials: "same-origin",
        body: JSON.stringify(body),
      });
      const payload = (await response.json()) as AuthResponse;

      if (!response.ok) {
        setErrorMessage(payload.errors?.[0]?.message || "ログインできませんでした。");
        return;
      }

      window.location.assign(payload.redirect_to || "/");
    } catch {
      setErrorMessage("ログイン処理でエラーが発生しました。時間を置いて再試行してください。");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="login-shell login" aria-label="U-motion login">
      <div className="login-container">
        <div className="login-heading">
          <img src={props.assets.logo} alt="U-motion" className="login-logo" />
          <div className="login-title-row">
            <img src={props.assets[method]} alt="" className="login-title-icon" />
            <h1>{methodLabels[method]}</h1>
          </div>
        </div>

        {errorMessage ? (
          <div className="login-error" role="alert">
            {errorMessage}
          </div>
        ) : null}

        <div className="login-main">
          {method === LOGIN_METHODS.EMAIL ? (
            <form className="login-form-panel" onSubmit={submitEmail}>
              <div className="login-input-card">
                <label className="login-field">
                  <span>メールアドレス</span>
                  <input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                  />
                </label>
                <label className="login-field">
                  <span>パスワード</span>
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                </label>
              </div>
              <div className="login-action-stack">
                <button className="primary-button login-submit" disabled={isSubmitting || !email || !password}>
                  U-motionにログイン
                </button>
                <button type="button" className="login-plain-button">
                  パスワードを忘れた方はこちら
                </button>
              </div>
            </form>
          ) : null}

          {method === LOGIN_METHODS.GOOGLE || method === LOGIN_METHODS.APPLE ? (
            <div className="login-form-panel">
              <div className="login-input-card login-provider-card">
                <button
                  type="button"
                  className="primary-button login-submit"
                  onClick={() => submitProvider(method)}
                  disabled={isSubmitting}
                >
                  {providerButtonLabels[method]}
                </button>
                <p className="login-help">認証画面がポップアップ表示されます。</p>
              </div>
            </div>
          ) : null}

          {method === LOGIN_METHODS.SMS ? (
            <form className="login-form-panel" onSubmit={smsSent ? submitSmsCode : submitSmsSend}>
              <div className="login-input-card">
                <p className="login-help">
                  {smsSent
                    ? "携帯電話に送信された確認コードを入力してください。"
                    : "携帯電話の電話番号を入力して確認コードを送信してください。"}
                </p>
                <label className="login-field">
                  <span>電話番号</span>
                  <input
                    type="tel"
                    autoComplete="tel"
                    inputMode="tel"
                    value={phoneNumber}
                    onChange={(event) => setPhoneNumber(event.target.value)}
                    disabled={smsSent}
                    required
                  />
                </label>
                {smsSent ? (
                  <label className="login-field">
                    <span>確認コード</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={smsCode}
                      onChange={(event) => setSmsCode(event.target.value)}
                      required
                      minLength={6}
                    />
                  </label>
                ) : null}
              </div>
              <div className="login-action-stack">
                {smsSent ? (
                  <button
                    key="sms-confirm"
                    className="primary-button login-submit"
                    disabled={isSubmitting || !confirmationResult || smsCode.length < 6}
                  >
                    ログイン
                  </button>
                ) : (
                  <button
                    key="sms-send"
                    id="phone-sign-in-button"
                    className="primary-button login-submit"
                    disabled={isSubmitting || !phoneNumber}
                  >
                    確認コードを送る
                  </button>
                )}
                {smsSent ? (
                  <>
                    <button
                      id="phone-resend-button"
                      type="button"
                      className="login-plain-button"
                      onClick={resendSmsCode}
                      disabled={isSubmitting}
                    >
                      確認コードを再送
                    </button>
                    <button
                      type="button"
                      className="login-plain-button"
                      onClick={changePhoneNumber}
                      disabled={isSubmitting}
                    >
                      電話番号を変更
                    </button>
                  </>
                ) : null}
              </div>
            </form>
          ) : null}

          <div className="login-alternatives">
            <span>別の方法でログイン</span>
            {visibleAlternativeMethods.map((candidate) => (
              <button
                key={candidate}
                type="button"
                className="login-method-button"
                onClick={() => selectMethod(candidate)}
              >
                <img src={props.assets[candidate]} alt="" className="login-method-icon" />
                {methodLabels[candidate]}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function toE164PhoneNumber(value: string) {
  const normalized = value.replace(/[^\d+]/g, "");
  if (normalized.startsWith("+")) return normalized;
  if (normalized.startsWith("0")) return `+81${normalized.slice(1)}`;
  return `+${normalized}`;
}

function providerErrorMessage(error: unknown, provider: ProviderLoginMethod) {
  const providerName = providerDisplayNames[provider];
  const code = errorCode(error);
  if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
    return "処理をキャンセルしました。";
  }
  if (code === "auth/popup-blocked") {
    return "ポップアップの表示に失敗しました。ブラウザの設定を確認してください。";
  }
  if (code === "auth/unauthorized-domain") {
    return "このドメインはFirebase Authenticationで許可されていません。Firebase ConsoleのAuthorized domainsを確認してください。";
  }
  if (code === "auth/invalid-continue-uri") {
    return "Firebase Authenticationの戻り先URLが許可されていません。Firebase ConsoleのAuthorized domainsを確認してください。";
  }
  if (code === "auth/invalid-api-key" || code === "auth/api-key-not-valid") {
    return "Firebase API keyが正しくありません。FIREBASE_API_KEYを確認してください。";
  }
  if (code === "auth/operation-not-allowed") {
    return `${providerName}ログインがFirebase Authenticationで有効化されていません。`;
  }
  if (error instanceof Error && error.message === "Firebase configuration is missing.") {
    return "Firebase設定が不足しています。FIREBASE_API_KEYとGOOGLE_PROJECT_IDを確認してください。";
  }

  return `${providerName}アカウントでの認証でエラーが発生しました。`;
}

function smsErrorMessage(error: unknown) {
  const code = errorCode(error);
  if (code === "auth/too-many-requests") {
    return "SMS送信でエラーが発生しました。時間を置いて再試行してください。";
  }
  if (code === "auth/invalid-phone-number") {
    return "電話番号の形式が正しくありません。";
  }
  if (code === "auth/invalid-verification-code") {
    return "確認コードが正しくありません。";
  }
  if (code === "auth/code-expired") {
    return "確認コードの有効期限が切れています。確認コードを再送してください。";
  }
  if (code === "auth/quota-exceeded") {
    return "SMS送信上限に達しました。時間を置いて再試行してください。";
  }
  if (code === "auth/operation-not-allowed") {
    return "電話番号ログインがFirebase Authenticationで有効化されていません。";
  }
  if (code === "auth/invalid-app-credential" || code === "auth/missing-app-credential") {
    return "Firebase Authenticationのアプリ確認に失敗しました。Authorized domainsとPhone認証の設定を確認してください。";
  }
  if (code === "auth/invalid-api-key" || code === "auth/api-key-not-valid") {
    return "Firebase API keyが正しくありません。FIREBASE_API_KEYを確認してください。";
  }
  if (error instanceof Error && error.message === "Firebase configuration is missing.") {
    return "Firebase設定が不足しています。FIREBASE_API_KEYとGOOGLE_PROJECT_IDを確認してください。";
  }

  return "SMS認証でエラーが発生しました。";
}

function errorCode(error: unknown) {
  return typeof error === "object" && error && "code" in error ? String(error.code) : "";
}

export function mountLoginApp() {
  const element = document.querySelector<HTMLElement>("[data-react-login]");
  if (!element) return;

  const firebaseConfig = JSON.parse(element.dataset.firebaseConfig || "{}") as FirebaseConfig;
  const assets = JSON.parse(element.dataset.loginAssets || "{}") as Record<LoginMethod | "logo", string>;
  const props: LoginAppProps = {
    sessionPath: element.dataset.sessionPath || "/session",
    firebaseSessionPath: element.dataset.firebaseSessionPath || "/session/firebase",
    returnTo: element.dataset.returnTo || "",
    csrfToken: document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content || "",
    firebaseConfig,
    assets,
  };

  const root = roots.get(element) || createRoot(element);
  roots.set(element, root);
  root.render(<LoginApp {...props} />);
}
